import { useState, useEffect } from "react";

/**
 * Breakpoint configuration for desktop detection
 * Screens below this width are considered "mobile" for features that require desktop
 */
export const DESKTOP_BREAKPOINT = 1024;

/**
 * Breakpoint for wide desktop layouts (e.g., split-column layouts)
 * Below this width, use single-column layouts
 */
export const WIDE_DESKTOP_BREAKPOINT = 1440;

/**
 * Hook to detect if the current viewport is desktop-sized
 * @param {number} breakpoint - Width threshold in pixels (default: 1024)
 * @returns {boolean} True if viewport width >= breakpoint
 */
export function useIsDesktop(breakpoint = DESKTOP_BREAKPOINT) {
  const [isDesktop, setIsDesktop] = useState(() => {
    // Initial value based on window width (SSR-safe)
    if (typeof window !== "undefined") {
      return window.innerWidth >= breakpoint;
    }
    return true; // Default to desktop for SSR
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);
    
    // Handler for media query changes
    const handleChange = (e) => {
      setIsDesktop(e.matches);
    };

    // Listen for changes - initial value already set in useState
    mediaQuery.addEventListener("change", handleChange);
    
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [breakpoint]);

  return isDesktop;
}

/**
 * Hook to detect if the current viewport is wide desktop (for split layouts)
 * @returns {boolean} True if viewport width >= WIDE_DESKTOP_BREAKPOINT
 */
export function useIsWideDesktop() {
  return useIsDesktop(WIDE_DESKTOP_BREAKPOINT);
}

/**
 * Generic media query hook for custom breakpoints
 * @param {string} query - CSS media query string
 * @returns {boolean} True if media query matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    
    const handleChange = (e) => {
      setMatches(e.matches);
    };

    // Listen for changes - initial value already set in useState
    mediaQuery.addEventListener("change", handleChange);
    
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [query]);

  return matches;
}
